
import { useState, useMemo, lazy, Suspense } from "react";
import { DateRange } from "react-day-picker";
import { subDays, startOfDay, endOfDay } from "date-fns";

import { useCouponDashboardMetrics } from "@/hooks/useCouponDashboardMetrics";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import CouponMetricsCardSection from "@/components/dashboard/CouponMetricsCardSection";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load the CouponChartsSection component to reduce initial load time
const CouponChartsSection = lazy(() => import("@/components/dashboard/CouponChartsSection"));

const Dashboard = () => {
  // Set default date range to last 7 days, ensuring full day coverage
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(subDays(new Date(), 6)),
    to: endOfDay(new Date()),
  });

  // Use the custom hook for fetching and processing coupon data
  const {
    couponMetrics,
    isLoadingMetrics,
    error,
    totalRevenue,
    totalCoupons,
    couponsUsed,
    couponsActive,
    couponsPending,
    conversionRate,
    averageTicket,
    couponROI,
    formatCurrency,
    revenueChartData,
    couponsChartData,
    statusChartData
  } = useCouponDashboardMetrics(dateRange);

  // Memoize props to prevent unnecessary re-renders
  const metricsCardProps = useMemo(() => ({
    totalRevenue,
    totalCoupons,
    couponsUsed,
    couponsActive,
    couponsPending,
    conversionRate,
    averageTicket,
    couponROI,
    formatCurrency,
    isLoadingMetrics
  }), [
    totalRevenue, 
    totalCoupons, 
    couponsUsed,
    couponsActive,
    couponsPending,
    conversionRate, 
    averageTicket, 
    couponROI,
    formatCurrency, 
    isLoadingMetrics
  ]);

  const chartsSectionProps = useMemo(() => ({
    revenueChartData,
    couponsChartData,
    statusChartData,
    isLoadingMetrics
  }), [revenueChartData, couponsChartData, statusChartData, isLoadingMetrics]);

  return (
    <div className="space-y-8">
      <DashboardHeader 
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      {/* Display error message if any data fetch failed */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Some data couldn't be loaded. Displaying available information.
          </AlertDescription>
        </Alert>
      )}

      <ErrorBoundary component="CouponMetricsCardSection" logError={true}>
        <CouponMetricsCardSection {...metricsCardProps} />
      </ErrorBoundary>

      <ErrorBoundary component="CouponChartsSection" logError={true}>
        <Suspense fallback={
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-[300px] w-full" />
            <Skeleton className="h-[300px] w-full" />
            <Skeleton className="h-[300px] w-full" />
          </div>
        }>
          <CouponChartsSection {...chartsSectionProps} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

export default Dashboard;
